/**
 * Battery Calculator Component
 * NFPA 72 compliant battery capacity calculator
 */

import { useState, useCallback } from 'react';
import { Battery, AlertCircle, CheckCircle2, Info, Download, RotateCcw } from 'lucide-react';
import styles from './BatteryCalculator.module.css';
import { useBatteryCalculator } from './useBatteryCalculator';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export interface BatteryCalculatorProps {
  initialValues?: {
    systemVoltage?: number;
    standbyCurrent?: number;
    alarmCurrent?: number;
    standbyTime?: number;
    alarmTime?: number;
    temperature?: number;
    batteryType?: 'sealed_lead_acid' | 'lithium';
  };
  onCalculate?: (result: any) => void;
}

export const BatteryCalculator: React.FC<BatteryCalculatorProps> = ({
  initialValues = {},
  onCalculate,
}) => {
  // Form state
  const [systemVoltage, setSystemVoltage] = useState(initialValues.systemVoltage ?? 24);
  const [standbyCurrent, setStandbyCurrent] = useState(initialValues.standbyCurrent ?? 0.5);
  const [alarmCurrent, setAlarmCurrent] = useState(initialValues.alarmCurrent ?? 2.0);
  const [standbyTime, setStandbyTime] = useState(initialValues.standbyTime ?? 24);
  const [alarmTime, setAlarmTime] = useState(initialValues.alarmTime ?? 5);
  const [temperature, setTemperature] = useState(initialValues.temperature ?? 77);
  const [batteryType, setBatteryType] = useState<'sealed_lead_acid' | 'lithium'>(
    initialValues.batteryType ?? 'sealed_lead_acid'
  );

  // Calculate results
  const { result, errors, isValid } = useBatteryCalculator({
    systemVoltage,
    standbyCurrent,
    alarmCurrent,
    standbyTime,
    alarmTime,
    temperature,
    batteryType,
  });

  // Handle calculate button click
  const handleCalculate = useCallback(() => {
    if (isValid && result && onCalculate) {
      onCalculate(result);
      
      // Track telemetry
      if (window.gtag) {
        window.gtag('event', 'battery_calc_compute', {
          system_voltage: systemVoltage,
          standby_current: standbyCurrent,
          alarm_current: alarmCurrent,
          battery_type: batteryType,
          required_capacity: result.requiredCapacity,
        });
      }
    }
  }, [isValid, result, onCalculate, systemVoltage, standbyCurrent, alarmCurrent, batteryType]);

  // Handle reset
  const handleReset = useCallback(() => {
    setSystemVoltage(24);
    setStandbyCurrent(0.5);
    setAlarmCurrent(2.0);
    setStandbyTime(24);
    setAlarmTime(5);
    setTemperature(77);
    setBatteryType('sealed_lead_acid');
    
    // Track telemetry
    if (window.gtag) {
      window.gtag('event', 'battery_calc_reset');
    }
  }, []);

  // Generate PDF content
  const generatePDFContent = useCallback(() => {
    if (!result) return null;

    return {
      title: 'NFPA 72 Battery Capacity Calculation',
      sections: [
        {
          title: 'Input Parameters',
          content: [
            `System Voltage: ${systemVoltage}V`,
            `Standby Current: ${standbyCurrent}A`,
            `Alarm Current: ${alarmCurrent}A`,
            `Standby Time: ${standbyTime} hours`,
            `Alarm Time: ${alarmTime} minutes`,
            `Temperature: ${temperature}°F`,
            `Battery Type: ${batteryType === 'sealed_lead_acid' ? 'Sealed Lead-Acid' : 'Lithium'}`,
          ],
        },
        {
          title: 'Calculation Results',
          content: [
            `Standby Load: ${result.standbyLoad.toFixed(2)} Ah`,
            `Alarm Load: ${result.alarmLoad.toFixed(2)} Ah`,
            `Total Load: ${result.totalLoad.toFixed(2)} Ah`,
            `Temperature Factor: ${result.temperatureFactor.toFixed(2)}`,
            `Aging Factor: ${result.agingFactor.toFixed(2)}`,
            `Required Capacity: ${result.requiredCapacity.toFixed(2)} Ah`,
            `Recommended Capacity: ${result.recommendedCapacity.toFixed(2)} Ah (with 20% safety margin)`,
            `NFPA 72 Compliant: ${result.nfpaCompliant ? 'Yes' : 'No'}`,
          ],
        },
        {
          title: 'Recommendations',
          content: result.recommendations,
        },
      ],
    };
  }, [result, systemVoltage, standbyCurrent, alarmCurrent, standbyTime, alarmTime, temperature, batteryType]);

  return (
    <div className={styles.calculator} role="main" aria-label="Battery Capacity Calculator">
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <Battery className={styles.headerIcon} size={32} />
          <div>
            <h1 className={styles.title}>Battery Capacity Calculator</h1>
            <p className={styles.subtitle}>NFPA 72 Compliant Battery Sizing</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            onClick={handleReset}
            className={styles.resetButton}
            aria-label="Reset calculator"
          >
            <RotateCcw size={18} />
            Reset
          </button>
          {result && (
            <button
              type="button"
              onClick={() => {
                const content = generatePDFContent();
                if (content) {
                  // Generate and download PDF as text report
                  const reportText = [
                    '=' .repeat(60),
                    content.title.toUpperCase(),
                    '=' .repeat(60),
                    `Generated: ${new Date().toLocaleString()}`,
                    '',
                    ...content.sections.flatMap(section => [
                      '-'.repeat(40),
                      section.title.toUpperCase(),
                      '-'.repeat(40),
                      ...section.content,
                      '',
                    ]),
                    '=' .repeat(60),
                    'This calculation follows NFPA 72 requirements.',
                    'Verify all calculations with a licensed engineer.',
                    '=' .repeat(60),
                  ].join('\n');
                  
                  const blob = new Blob([reportText], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `battery-calculation-${new Date().toISOString().split('T')[0]}.txt`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  
                  // Track telemetry
                  if (window.gtag) {
                    window.gtag('event', 'battery_calc_export_pdf');
                  }
                }
              }}
              className={styles.resetButton}
              aria-label="Export to PDF"
            >
              <Download size={18} />
              Export PDF
            </button>
          )}
          {result && (
            <button
              type="button"
              onClick={() => {
                // Generate CSV export
                const csvRows = [
                  ['NFPA 72 Battery Capacity Calculation'],
                  ['Generated', new Date().toLocaleString()],
                  [''],
                  ['INPUT PARAMETERS'],
                  ['System Voltage (V)', systemVoltage.toString()],
                  ['Standby Current (A)', standbyCurrent.toString()],
                  ['Alarm Current (A)', alarmCurrent.toString()],
                  ['Standby Time (hours)', standbyTime.toString()],
                  ['Alarm Time (minutes)', alarmTime.toString()],
                  ['Temperature (°F)', temperature.toString()],
                  ['Battery Type', batteryType === 'sealed_lead_acid' ? 'Sealed Lead-Acid' : 'Lithium'],
                  [''],
                  ['CALCULATION RESULTS'],
                  ['Standby Load (Ah)', result.standbyLoad.toFixed(2)],
                  ['Alarm Load (Ah)', result.alarmLoad.toFixed(2)],
                  ['Total Load (Ah)', result.totalLoad.toFixed(2)],
                  ['Temperature Factor', result.temperatureFactor.toFixed(2)],
                  ['Aging Factor', result.agingFactor.toFixed(2)],
                  ['Required Capacity (Ah)', result.requiredCapacity.toFixed(2)],
                  ['Recommended Capacity (Ah)', result.recommendedCapacity.toFixed(2)],
                  ['NFPA 72 Compliant', result.nfpaCompliant ? 'Yes' : 'No'],
                  [''],
                  ['RECOMMENDATIONS'],
                  ...result.recommendations.map(r => [r]),
                ];
                
                const csvContent = csvRows.map(row => row.join(',')).join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `battery-calculation-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                if (window.gtag) {
                  window.gtag('event', 'battery_calc_export_csv');
                }
              }}
              className={styles.resetButton}
              aria-label="Export to CSV"
            >
              <Download size={18} />
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className={styles.infoBanner}>
        <Info size={18} />
        <p>
          This calculator determines the required battery capacity per NFPA 72 requirements,
          accounting for standby time, alarm time, temperature derating, and aging factors.
        </p>
      </div>

      <div className={styles.content}>
        {/* Input Form */}
        <div className={styles.inputSection}>
          <h2 className={styles.sectionTitle}>Input Parameters</h2>

          {/* System Voltage */}
          <div className={styles.formGroup}>
            <label htmlFor="systemVoltage" className={styles.label}>
              System Voltage
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputGroup}>
              <select
                id="systemVoltage"
                value={systemVoltage}
                onChange={(e) => setSystemVoltage(Number(e.target.value))}
                className={styles.select}
                aria-required="true"
              >
                <option value={12}>12V</option>
                <option value={24}>24V</option>
              </select>
            </div>
            {errors.systemVoltage && (
              <p className={styles.error} role="alert">
                <AlertCircle size={14} />
                {errors.systemVoltage}
              </p>
            )}
          </div>

          {/* Standby Current */}
          <div className={styles.formGroup}>
            <label htmlFor="standbyCurrent" className={styles.label}>
              Standby Current
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputGroup}>
              <input
                type="number"
                id="standbyCurrent"
                value={standbyCurrent}
                onChange={(e) => setStandbyCurrent(Number(e.target.value))}
                min="0"
                step="0.1"
                className={styles.input}
                aria-required="true"
                aria-describedby="standbyCurrent-help"
              />
              <span className={styles.unit}>A</span>
            </div>
            <p id="standbyCurrent-help" className={styles.helpText}>
              Total current draw during normal (non-alarm) operation
            </p>
            {errors.standbyCurrent && (
              <p className={styles.error} role="alert">
                <AlertCircle size={14} />
                {errors.standbyCurrent}
              </p>
            )}
          </div>

          {/* Alarm Current */}
          <div className={styles.formGroup}>
            <label htmlFor="alarmCurrent" className={styles.label}>
              Alarm Current
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputGroup}>
              <input
                type="number"
                id="alarmCurrent"
                value={alarmCurrent}
                onChange={(e) => setAlarmCurrent(Number(e.target.value))}
                min="0"
                step="0.1"
                className={styles.input}
                aria-required="true"
                aria-describedby="alarmCurrent-help"
              />
              <span className={styles.unit}>A</span>
            </div>
            <p id="alarmCurrent-help" className={styles.helpText}>
              Total current draw during alarm condition
            </p>
            {errors.alarmCurrent && (
              <p className={styles.error} role="alert">
                <AlertCircle size={14} />
                {errors.alarmCurrent}
              </p>
            )}
          </div>

          {/* Standby Time */}
          <div className={styles.formGroup}>
            <label htmlFor="standbyTime" className={styles.label}>
              Standby Time
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputGroup}>
              <input
                type="number"
                id="standbyTime"
                value={standbyTime}
                onChange={(e) => setStandbyTime(Number(e.target.value))}
                min="24"
                step="1"
                className={styles.input}
                aria-required="true"
                aria-describedby="standbyTime-help"
              />
              <span className={styles.unit}>hours</span>
            </div>
            <p id="standbyTime-help" className={styles.helpText}>
              NFPA 72 requires minimum 24 hours (48 hours for some applications)
            </p>
            {errors.standbyTime && (
              <p className={styles.error} role="alert">
                <AlertCircle size={14} />
                {errors.standbyTime}
              </p>
            )}
          </div>

          {/* Alarm Time */}
          <div className={styles.formGroup}>
            <label htmlFor="alarmTime" className={styles.label}>
              Alarm Time
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputGroup}>
              <input
                type="number"
                id="alarmTime"
                value={alarmTime}
                onChange={(e) => setAlarmTime(Number(e.target.value))}
                min="5"
                step="1"
                className={styles.input}
                aria-required="true"
                aria-describedby="alarmTime-help"
              />
              <span className={styles.unit}>minutes</span>
            </div>
            <p id="alarmTime-help" className={styles.helpText}>
              NFPA 72 requires minimum 5 minutes (15 minutes for some applications)
            </p>
            {errors.alarmTime && (
              <p className={styles.error} role="alert">
                <AlertCircle size={14} />
                {errors.alarmTime}
              </p>
            )}
          </div>

          {/* Temperature */}
          <div className={styles.formGroup}>
            <label htmlFor="temperature" className={styles.label}>
              Temperature
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputGroup}>
              <input
                type="number"
                id="temperature"
                value={temperature}
                onChange={(e) => setTemperature(Number(e.target.value))}
                min="-40"
                max="140"
                step="1"
                className={styles.input}
                aria-required="true"
                aria-describedby="temperature-help"
              />
              <span className={styles.unit}>°F</span>
            </div>
            <p id="temperature-help" className={styles.helpText}>
              Ambient temperature affects battery capacity
            </p>
            {errors.temperature && (
              <p className={styles.error} role="alert">
                <AlertCircle size={14} />
                {errors.temperature}
              </p>
            )}
          </div>

          {/* Battery Type */}
          <div className={styles.formGroup}>
            <label htmlFor="batteryType" className={styles.label}>
              Battery Type
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputGroup}>
              <select
                id="batteryType"
                value={batteryType}
                onChange={(e) => setBatteryType(e.target.value as 'sealed_lead_acid' | 'lithium')}
                className={styles.select}
                aria-required="true"
              >
                <option value="sealed_lead_acid">Sealed Lead-Acid</option>
                <option value="lithium">Lithium</option>
              </select>
            </div>
            {errors.batteryType && (
              <p className={styles.error} role="alert">
                <AlertCircle size={14} />
                {errors.batteryType}
              </p>
            )}
          </div>
        </div>

        {/* Results Section */}
        <div className={styles.resultsSection}>
          <h2 className={styles.sectionTitle}>Calculation Results</h2>

          {!isValid && Object.keys(errors).length > 0 && (
            <div className={styles.validationAlert} role="alert">
              <AlertCircle size={20} />
              <div>
                <strong>Please correct the following errors:</strong>
                <ul>
                  {Object.values(errors).map((error: string, index: number) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {isValid && result && (
            <>
              {/* Compliance Status */}
              <div className={`${styles.complianceCard} ${result.nfpaCompliant ? styles.compliant : styles.nonCompliant}`}>
                {result.nfpaCompliant ? (
                  <>
                    <CheckCircle2 size={24} />
                    <div>
                      <strong>NFPA 72 Compliant</strong>
                      <p>Configuration meets all requirements</p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle size={24} />
                    <div>
                      <strong>Non-Compliant</strong>
                      <p>Configuration does not meet NFPA 72 requirements</p>
                    </div>
                  </>
                )}
              </div>

              {/* Load Calculations */}
              <div className={styles.resultCard}>
                <h3 className={styles.resultCardTitle}>Load Calculations</h3>
                <div className={styles.resultGrid}>
                  <div className={styles.resultItem}>
                    <span className={styles.resultLabel}>Standby Load</span>
                    <span className={styles.resultValue}>
                      {result.standbyLoad.toFixed(2)} <span className={styles.resultUnit}>Ah</span>
                    </span>
                  </div>
                  <div className={styles.resultItem}>
                    <span className={styles.resultLabel}>Alarm Load</span>
                    <span className={styles.resultValue}>
                      {result.alarmLoad.toFixed(2)} <span className={styles.resultUnit}>Ah</span>
                    </span>
                  </div>
                  <div className={styles.resultItem}>
                    <span className={styles.resultLabel}>Total Load</span>
                    <span className={styles.resultValue}>
                      {result.totalLoad.toFixed(2)} <span className={styles.resultUnit}>Ah</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Derating Factors */}
              <div className={styles.resultCard}>
                <h3 className={styles.resultCardTitle}>Derating Factors</h3>
                <div className={styles.resultGrid}>
                  <div className={styles.resultItem}>
                    <span className={styles.resultLabel}>Temperature Factor</span>
                    <span className={styles.resultValue}>{result.temperatureFactor.toFixed(2)}</span>
                  </div>
                  <div className={styles.resultItem}>
                    <span className={styles.resultLabel}>Aging Factor</span>
                    <span className={styles.resultValue}>{result.agingFactor.toFixed(2)}</span>
                  </div>
                  <div className={styles.resultItem}>
                    <span className={styles.resultLabel}>Safety Margin</span>
                    <span className={styles.resultValue}>{((result.safetyMargin - 1) * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>

              {/* Required Capacity */}
              <div className={styles.resultCard} style={{ backgroundColor: 'var(--color-primary-50)' }}>
                <h3 className={styles.resultCardTitle}>Required Battery Capacity</h3>
                <div className={styles.capacityResults} aria-live="polite">
                  <div className={styles.capacityItem}>
                    <span className={styles.capacityLabel}>Minimum Required</span>
                    <span className={styles.capacityValue}>
                      {result.requiredCapacity.toFixed(2)} <span className={styles.capacityUnit}>Ah</span>
                    </span>
                  </div>
                  <div className={styles.capacityItem} style={{ borderTop: '2px solid var(--color-primary-200)' }}>
                    <span className={styles.capacityLabel}>Recommended (with safety margin)</span>
                    <span className={styles.capacityValue} style={{ fontSize: '2rem', color: 'var(--color-primary-600)' }}>
                      {result.recommendedCapacity.toFixed(2)} <span className={styles.capacityUnit}>Ah</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              {result.recommendations.length > 0 && (
                <div className={styles.recommendationsCard}>
                  <h3 className={styles.resultCardTitle}>
                    <Info size={20} />
                    Recommendations
                  </h3>
                  <ul className={styles.recommendationsList}>
                    {result.recommendations.map((rec: string, index: number) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatteryCalculator;
